import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@pg-env/environment';
import { format } from 'date-fns';
import { TableCellTypes } from '@nttp/enum/table-cell-type-enum';

interface ICombinePhoneNumber {
  phoneNumber: string;
  callingCodeId: string;
}

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private api: string;
  private identityapi: string;

  constructor(protected http: HttpClient) {
    this.api = environment.customerApiUrl;
    this.identityapi = environment.identityApiUrl;
  }

  public getCurrencies(): Observable<any> {
    const url: string = this.api + '/trade/get-trade-currencies';
    return this.http.get<any>(url);
  }

  public getAccessLevels(): Observable<any> {
    const url: string = this.api + '/customer/access-level-list';
    return this.http.get<any>(url);
  }

  public combinePhonenumber(combinePhoneNumberRequest: ICombinePhoneNumber): Observable<any> {
    return this.http.post<any>(`${this.api}/customer/convert-phonenumber`, combinePhoneNumberRequest);
  }

  
  public parsePhonenumber(parsePhoneNumberRequest: ICombinePhoneNumber): Observable<any> {
    return this.http.post<any>(`${this.api}/customer/parse-phonenumber`, parsePhoneNumberRequest);
  }

  public getAboutUs(): Observable<any> {
    const url: string = this.api + '/customer/hdyhau-list';
    return this.http.get<any>(url);
  }

  public getTradeType(valueDate: string, inputDate: string, currencyCodes: Array<string>): Observable<any> {
    const data = {
      valueDate: valueDate,
      inputDate: inputDate,
      currencyCode: currencyCodes
    };
    return this.http.post(`${this.api}/customer/get-tradetype-by-date`, data);
  }

  public getHolidays(fromDate: string, toDate: string, currencyCodes: Array<string>): Observable<any> {
    const body = {
      fromDate: fromDate, // format yyyyMMdd and can not null
      toDate: toDate, // format yyyyMMdd and can not null
      currencyCodes: currencyCodes // list currency and can not null
    };

    const url: string = this.api + '/non-trading-day/get-holidays';
    return this.http.post<any>(url, body);
  }

  updatePassword(params): Observable<any> {
    return this.http.post(`${this.identityapi}/account/change-password`, params);
  }

  updateMemorable(params): Observable<any> {
    return this.http.post(`${this.identityapi}/account/change-memorable-number`, params);
  }

  public replaceValue(value: any) {
    if (parseFloat(value) === 0) {
      return '0.00';
    }
    if (!value) {
      return;
    }

    if (isNaN(parseFloat(value))) {
      return value;
    }

    const parts = parseFloat(value).toFixed(2).toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parseFloat(value) > 0 ? parts.join('.') : `(${parts.join('.').replace('-', '')})`;
  }

  changeFilter(data: any, columns: any): any {
    let filteredData: Array<any> = data;

    columns.forEach((column: any) => {
      if (column.filtering && !!column.filtering.filterString) {
        filteredData = filteredData.filter((item: any) => {
          if (column.filtering.filterString && column.type === TableCellTypes.Date) {
            return (
              format(new Date(item[column.name]), 'DD/MM/YYYY') ===
              format(new Date(column.filtering.filterString), 'DD/MM/YYYY')
            );
          }
          if (column.type === TableCellTypes.Number || column.type === TableCellTypes.Rate) {
            if (column.filtering.filterString && !isNaN(parseFloat(column.filtering.filterString))) {
              const filterValue = column.filtering.filterString.split(',').join('');
              const compareValue = item[column.name] ? item[column.name].toString() : '0.00';
              return compareValue.indexOf(filterValue) !== -1;
            }
          }

          const filterString = column.filtering.filterString.toLowerCase();
          const valueItem = (item[column.name] + '').toLowerCase();
          return valueItem.match(filterString);
        });
      }
    });

    return filteredData;
  }

  changeSort(data: any, config: any): Array<any> {
    if (!config.sorting) {
      return data;
    }
    const columns = config.sorting.columns || [];
    let columnName: string = void 0;

    let sort: string = void 0;
    let type: string = void 0;

    for (let i = 0; i < columns.length; i++) {
      if (columns[i].sort && columns[i].sort !== '' && columns[i].sort !== false) {
        columnName = columns[i].name;
        sort = columns[i].sort;
        type = columns[i].type;
      }
    }

    if (!columnName) {
      return data;
    }

    return data.sort((previous: any, current: any) => {
      let previousVal: any;
      let currentVal: any;

      if (type === TableCellTypes.Date) {
        previousVal = new Date(previous[columnName]).setHours(0, 0, 0, 0);
        currentVal = new Date(current[columnName]).setHours(0, 0, 0, 0);
      } else if (type === TableCellTypes.Number || type === TableCellTypes.Rate) {

        previousVal = previous[columnName] && !isNaN(previous[columnName]) ? parseFloat(previous[columnName]) : null;
        currentVal = current[columnName] && !isNaN(current[columnName]) ? parseFloat(current[columnName]) : null;

      } else if (typeof previous[columnName] === 'string') {
        previousVal = previous[columnName] ? previous[columnName] : '';
        currentVal = current[columnName] ? current[columnName] : '';
      }

      if (previousVal > currentVal) {
        return sort === 'desc' ? -1 : 1;
      } else if (previousVal < currentVal) {
        return sort === 'asc' ? -1 : 1;
      }
      return 0;
    });
  }

  public isAccountOperational(): Observable<any> {
    return this.http.get(`${this.api}/customer/is-account-operational`);
  }

  // TODO 13/07/2021
  // Create a copy of 'downloadPDF' and rename to 'downloadFile'
  // with Type is Enum
  // updated in PaymentImport first and will implement in the other places next sprint

  public downloadFile(data, fileName, type) {
    const ieEDGE = navigator.userAgent.match(/Edge/g);
    const ie = navigator.userAgent.match(/.NET/g);
    const oldIE = navigator.userAgent.match(/MSIE/g);
    const ios = navigator.userAgent.match(/iPad|iPhone|iPod/g);
    const blob = new Blob([data], { type: type });
    if (ie || oldIE || ieEDGE) {
      window.navigator.msSaveBlob(blob, fileName);
    } else if (ios) {
      //reserve for processing ios in the future IOS/ipadOS version if there is any issue found
      this.downloadTheBlob(blob, fileName);
    } else {
      this.downloadTheBlob(blob, fileName);
    }
  }

  public downloadTheBlob(blob, fileName) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    });
  }

  public makeUserFriendly(rawMsg: string): string {
    if (rawMsg === undefined) {
      return '';
    }

    var friendlyMsg = rawMsg.replace(/_(\{){0,1}[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}(\}){0,1}./g, '.');
    var pos = friendlyMsg.lastIndexOf('\/');
		if (pos != -1) {
    	friendlyMsg = friendlyMsg.substring(pos + 1);
    }    
    
    return friendlyMsg;
  }
}
